import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { HandshakeIcon, UsersIcon, HeartIcon, TargetIcon, EyeIcon, ArrowLeftIcon } from 'lucide-react';
export function NosotrosPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const features = [{
    icon: <HandshakeIcon className="w-6 h-6 text-brand-royal" data-id="element-683" />,
    title: 'Fortalecimiento Institucional',
    description: 'Apoyo técnico, operativo, administrativo y documental para mejorar la gestión, gobernanza y sostenibilidad de organizaciones sociales.'
  }, {
    icon: <UsersIcon className="w-6 h-6 text-brand-royal" data-id="element-684" />,
    title: 'Transformación Digital',
    description: 'Provisión de plataformas digitales, sistemas de información y soluciones de automatización para modernizar y optimizar las organizaciones.'
  }, {
    icon: <HeartIcon className="w-6 h-6 text-brand-royal" data-id="element-685" />,
    title: 'Articulación Colaborativa',
    description: 'Vinculación entre organizaciones sociales, empresas, academia, sector público y organismos nacionales e internacionales.'
  }];
  return <div className="pt-20" data-id="element-686">
      {/* Hero Banner */}
      <section className="relative py-24 md:py-32 bg-brand-navy overflow-hidden" data-id="element-687">
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-688">
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_bottom_left,_var(--tw-gradient-stops))] from-brand-royal/30 via-brand-navy to-brand-navy" data-id="element-689"></div>
          <motion.div animate={{
          y: [0, -15, 0],
          x: [0, 8, 0]
        }} transition={{
          duration: 6,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="absolute top-1/3 right-1/4 w-64 h-64 bg-brand-gold/10 rounded-full blur-3xl" data-id="element-690" />
          <motion.div animate={{
          y: [0, 20, 0]
        }} transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1
        }} className="absolute bottom-1/4 left-1/3 w-80 h-80 bg-brand-crimson/8 rounded-full blur-3xl" data-id="element-691" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-692">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7
        }} className="max-w-3xl" data-id="element-693">
            <a href="/" className="inline-flex items-center gap-2 text-white/60 hover:text-brand-gold transition-colors text-sm mb-8 group" data-id="element-694">
              <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" data-id="element-695" />
              Volver al inicio
            </a>
            <h1 className="text-5xl md:text-7xl font-heading font-bold text-white mb-6 leading-tight" data-id="element-696">
              Quiénes{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gold to-yellow-200" data-id="element-697">
                Somos
              </span>
            </h1>
            <p className="text-xl text-white/75 leading-relaxed max-w-2xl" data-id="element-698">
              La Fundación RedOrg es una persona jurídica de derecho privado,
              sin fines de lucro, de finalidad social y de interés general,
              dedicada al fortalecimiento del sector social en Ecuador.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Objeto Social */}
      <section className="py-24 bg-white" data-id="element-699">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-700">
          <div data-id="element-701">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.7
          }} data-id="element-702">
              <h2 className="text-brand-royal font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-703">
                Nuestro Propósito
              </h2>
              <h3 className="text-4xl md:text-5xl font-heading font-bold text-brand-navy mb-6 leading-tight" data-id="element-704">
                El puente entre la{' '}
                <span className="text-brand-crimson" data-id="element-705">voluntad</span> y la{' '}
                <span className="text-brand-gold" data-id="element-706">acción</span>
              </h3>

              <div className="space-y-6 text-gray-600 text-lg leading-relaxed" data-id="element-707">
                <p data-id="element-708">
                  Nuestro propósito es promover, apoyar y ejecutar acciones de
                  fortalecimiento institucional, tecnológico, operativo y
                  administrativo de fundaciones, corporaciones, asociaciones,
                  cámaras, redes y demás organizaciones sociales, contribuyendo
                  a su desarrollo, sostenibilidad e impacto positivo en la
                  sociedad.
                </p>
                <p data-id="element-709">
                  Trabajamos mediante programas, proyectos, servicios de apoyo,
                  asistencia técnica, capacitación, innovación, transformación
                  digital, vinculación interinstitucional, cooperación nacional
                  e internacional y generación de alianzas con empresas,
                  academia, sector público y comunidad.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-710">
                {features.map((feature, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.15,
                duration: 0.5
              }} className="bg-brand-lightGray rounded-2xl p-8 border border-gray-100" data-id="element-711">
                    <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center border border-gray-100 shadow-sm mb-5" data-id="element-712">
                      {feature.icon}
                    </div>
                    <h4 className="text-xl font-heading font-semibold text-brand-navy mb-2" data-id="element-713">
                      {feature.title}
                    </h4>
                    <p className="text-gray-600 leading-relaxed" data-id="element-714">
                      {feature.description}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Misión y Visión */}
      <section className="py-24 bg-brand-navy relative overflow-hidden" data-id="element-715">
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(#F5B731 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-716"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-royal/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" data-id="element-717"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-brand-crimson/10 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" data-id="element-718"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-719">
          <div className="text-center mb-16" data-id="element-720">
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-brand-lightBlue font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-721">
              Nuestro Norte
            </motion.h2>
            <motion.h3 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl font-heading font-bold text-white" data-id="element-722">
              Misión y Visión
            </motion.h3>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-723">
            {/* Misión */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} data-id="element-724">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-10 h-full hover:bg-white/10 transition-colors duration-500" data-id="element-725">
                <div className="flex items-center gap-4 mb-8" data-id="element-726">
                  <div className="w-14 h-14 rounded-2xl bg-brand-gold/20 flex items-center justify-center border border-brand-gold/30" data-id="element-727">
                    <TargetIcon className="w-7 h-7 text-brand-gold" data-id="element-728" />
                  </div>
                  <div data-id="element-729">
                    <span className="text-brand-gold font-semibold text-xs uppercase tracking-widest" data-id="element-730">
                      Artículo Estatutario
                    </span>
                    <h4 className="text-3xl font-heading font-bold text-white" data-id="element-731">
                      Misión
                    </h4>
                  </div>
                </div>
                <p className="text-white/85 text-lg leading-relaxed" data-id="element-732">
                  La Fundación REDORG tiene como misión{' '}
                  <strong className="text-brand-gold" data-id="element-733">
                    fortalecer a las organizaciones sin fines de lucro
                  </strong>{' '}
                  mediante apoyo tecnológico, operativo, institucional y
                  articulador, promoviendo su modernización, sostenibilidad,
                  colaboración y capacidad de generar{' '}
                  <strong className="text-brand-gold" data-id="element-734">
                    impacto positivo en la sociedad
                  </strong>
                  .
                </p>
                <div className="mt-8 h-1 w-20 bg-gradient-to-r from-brand-gold to-brand-gold/0 rounded-full" data-id="element-735"></div>
              </div>
            </motion.div>

            {/* Visión */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6,
            delay: 0.15
          }} data-id="element-736">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-10 h-full hover:bg-white/10 transition-colors duration-500" data-id="element-737">
                <div className="flex items-center gap-4 mb-8" data-id="element-738">
                  <div className="w-14 h-14 rounded-2xl bg-brand-lightBlue/20 flex items-center justify-center border border-brand-lightBlue/30" data-id="element-739">
                    <EyeIcon className="w-7 h-7 text-brand-lightBlue" data-id="element-740" />
                  </div>
                  <div data-id="element-741">
                    <span className="text-brand-lightBlue font-semibold text-xs uppercase tracking-widest" data-id="element-742">
                      Artículo Estatutario
                    </span>
                    <h4 className="text-3xl font-heading font-bold text-white" data-id="element-743">
                      Visión
                    </h4>
                  </div>
                </div>
                <p className="text-white/85 text-lg leading-relaxed" data-id="element-744">
                  Ser una fundación{' '}
                  <strong className="text-brand-lightBlue" data-id="element-745">
                    referente en Ecuador
                  </strong>{' '}
                  y, progresivamente, en otros países, por su capacidad de
                  conectar, fortalecer y modernizar a las organizaciones sin
                  fines de lucro, promoviendo{' '}
                  <strong className="text-brand-lightBlue" data-id="element-746">
                    ecosistemas colaborativos
                  </strong>{' '}
                  entre organizaciones, empresas y comunidad para el desarrollo
                  social sostenible.
                </p>
                <div className="mt-8 h-1 w-20 bg-gradient-to-r from-brand-lightBlue to-brand-lightBlue/0 rounded-full" data-id="element-747"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}